import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  applyHeadBrandingAssets,
  applyInstitutionalBrandingAssets,
} from "../../services/local/institutionalBranding";
import { setDocumentTitle } from "../../services/local/pageTitle";
import { supabase } from "../../services/supabase/client";
import {
  applyPublicKitchenBoardAction,
  getPublicKitchenBoardByPairingCode,
} from "../../services/supabase/qrService";

const KITCHEN_FALLBACK_REFRESH_INTERVAL_MS = 8000;
const KITCHEN_PAIRING_STORAGE_KEY = "mozzoapp:public-kitchen:pairing-code";

const readStoredKitchenCode = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return String(window.localStorage.getItem(KITCHEN_PAIRING_STORAGE_KEY) || "").trim();
};

const normalizePairingCodeInput = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "");

const formatPreparationNumber = (value) =>
  `#${String(Math.max(0, Number(value || 0))).padStart(3, "0")}`;

const formatBoardTimestamp = (value) => {
  if (!value) {
    return "Sin sincronizacion todavia";
  }

  try {
    return new Date(value).toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return "Sin sincronizacion todavia";
  }
};

const formatElapsed = (value) => {
  if (!value) {
    return "Recien ingresado";
  }

  const startedAt = new Date(value).getTime();

  if (!Number.isFinite(startedAt)) {
    return "Recien ingresado";
  }

  const diffMinutes = Math.max(0, Math.round((Date.now() - startedAt) / 60000));

  if (diffMinutes < 1) {
    return "Hace instantes";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} min`;
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const getOrderStageMeta = (stage) => {
  if (stage === "new") {
    return {
      label: "Nuevo en cocina",
      hint: "Todavia no fue tomado por cocina.",
    };
  }

  if (stage === "preparing") {
    return {
      label: "En preparacion",
      hint: "La cocina ya lo tomo y lo esta preparando.",
    };
  }

  return {
    label: "Listo para entregar",
    hint: "Ya termino cocina y espera retiro o entrega.",
  };
};

const getKitchenTargetStage = (action) => {
  if (action === "ready") {
    return "ready";
  }

  return "preparing";
};

const updateKitchenBoardAfterAction = (currentBoard, order, action, updatedAt) => {
  if (!currentBoard) {
    return currentBoard;
  }

  const orderId = String(order?.id || "").trim();

  if (!orderId) {
    return currentBoard;
  }

  const nextUpdatedAt = updatedAt || new Date().toISOString();
  const targetStage = getKitchenTargetStage(action);
  const nextOrder = {
    ...order,
    stage: targetStage,
  };

  const collectOrder = (list = []) =>
    Array.isArray(list)
      ? list.find((item) => String(item?.id || "").trim() === orderId) || null
      : null;

  const currentOrder =
    collectOrder(currentBoard.newOrders) ||
    collectOrder(currentBoard.preparingOrders) ||
    collectOrder(currentBoard.readyOrders) ||
    null;

  const normalizedOrder = {
    ...(currentOrder || {}),
    ...nextOrder,
  };

  const removeOrder = (list = []) =>
    (Array.isArray(list) ? list : []).filter(
      (item) => String(item?.id || "").trim() !== orderId
    );

  const nextNewOrders = removeOrder(currentBoard.newOrders);
  const nextPreparingOrders = removeOrder(currentBoard.preparingOrders);
  const nextReadyOrders = removeOrder(currentBoard.readyOrders);

  if (targetStage === "preparing") {
    nextPreparingOrders.unshift(normalizedOrder);
  } else {
    nextReadyOrders.unshift(normalizedOrder);
  }

  return {
    ...currentBoard,
    updatedAt: nextUpdatedAt,
    newOrders: nextNewOrders,
    preparingOrders: nextPreparingOrders,
    readyOrders: nextReadyOrders,
  };
};

function CocinaPublica() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCode = useMemo(() => {
    const queryCode = normalizePairingCodeInput(searchParams.get("code"));

    if (queryCode) {
      return queryCode;
    }

    return normalizePairingCodeInput(readStoredKitchenCode());
  }, [searchParams]);
  const [codeInput, setCodeInput] = useState(initialCode);
  const [linkedCode, setLinkedCode] = useState(initialCode);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(Boolean(initialCode));
  const [errorMessage, setErrorMessage] = useState("");
  const [screenMessage, setScreenMessage] = useState({ type: "", text: "" });
  const [pendingActionKey, setPendingActionKey] = useState("");
  const boardRequestRef = useRef({ code: "", promise: null, requestId: 0 });

  const branch = board?.branch || null;
  const newOrders = Array.isArray(board?.newOrders) ? board.newOrders : [];
  const preparingOrders = Array.isArray(board?.preparingOrders)
    ? board.preparingOrders
    : [];
  const readyOrders = Array.isArray(board?.readyOrders) ? board.readyOrders : [];
  const branchIconUrl =
    branch?.iconLogoUrl || branch?.loadingLogoUrl || branch?.logoCompactUrl || "";
  const summary = {
    total: newOrders.length + preparingOrders.length + readyOrders.length,
    new: newOrders.length,
    preparing: preparingOrders.length,
    ready: readyOrders.length,
  };

  const persistLinkedCode = useCallback(
    (nextCode) => {
      const safeCode = normalizePairingCodeInput(nextCode);

      if (typeof window !== "undefined") {
        if (safeCode) {
          window.localStorage.setItem(KITCHEN_PAIRING_STORAGE_KEY, safeCode);
        } else {
          window.localStorage.removeItem(KITCHEN_PAIRING_STORAGE_KEY);
        }
      }

      setSearchParams(safeCode ? { code: safeCode } : {}, { replace: true });
      setLinkedCode(safeCode);
      setCodeInput(safeCode);
    },
    [setSearchParams]
  );

  const loadBoard = useCallback(async (pairingCode, { quiet = false } = {}) => {
    const safeCode = normalizePairingCodeInput(pairingCode);

    if (!safeCode) {
      setBoard(null);
      setLoading(false);
      return;
    }

    if (
      boardRequestRef.current.promise &&
      boardRequestRef.current.code === safeCode
    ) {
      return boardRequestRef.current.promise;
    }

    const requestId = boardRequestRef.current.requestId + 1;

    if (!quiet) {
      setLoading(true);
    }

    const requestPromise = (async () => {
      try {
        const nextBoard = await getPublicKitchenBoardByPairingCode(safeCode);

        if (boardRequestRef.current.requestId === requestId) {
          setBoard(nextBoard);
          setErrorMessage("");
        }
      } catch (error) {
        if (boardRequestRef.current.requestId === requestId) {
          if (!quiet) {
            setBoard(null);
          }

          setErrorMessage(
            error?.message || "No pudimos abrir la cocina publica con ese codigo."
          );
        }
      } finally {
        if (boardRequestRef.current.requestId === requestId && !quiet) {
          setLoading(false);
        }
      }
    })();

    boardRequestRef.current = {
      code: safeCode,
      promise: requestPromise,
      requestId,
    };

    try {
      return await requestPromise;
    } finally {
      if (boardRequestRef.current.promise === requestPromise) {
        boardRequestRef.current = {
          ...boardRequestRef.current,
          promise: null,
        };
      }
    }
  }, []);

  useEffect(() => {
    const queryCode = normalizePairingCodeInput(searchParams.get("code"));

    if (!queryCode) {
      return;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(KITCHEN_PAIRING_STORAGE_KEY, queryCode);
    }

    setLinkedCode((currentValue) => (currentValue === queryCode ? currentValue : queryCode));
    setCodeInput((currentValue) => (currentValue === queryCode ? currentValue : queryCode));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    if (!linkedCode) {
      setBoard(null);
      setLoading(false);
      return undefined;
    }

    const runLoad = async (quiet = false) => {
      try {
        if (!cancelled) {
          await loadBoard(linkedCode, { quiet });
        }
      } catch (error) {
        // loadBoard ya muestra el error.
      }
    };

    runLoad(false);

    const intervalId = window.setInterval(() => {
      runLoad(true);
    }, KITCHEN_FALLBACK_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [linkedCode, loadBoard]);

  useEffect(() => {
    const branchId = String(board?.branch?.id || "").trim();

    if (!branchId || !linkedCode || !supabase) {
      return undefined;
    }

    const channel = supabase
      .channel(`public-kitchen:${branchId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "branch_display_state",
          filter: `branch_id=eq.${branchId}`,
        },
        () => {
          loadBoard(linkedCode, { quiet: true });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [board?.branch?.id, linkedCode, loadBoard]);

  useEffect(() => {
    if (branch?.name) {
      setDocumentTitle("Cocina", branch.name);
    } else {
      setDocumentTitle("Cocina");
    }

    if (branchIconUrl) {
      applyHeadBrandingAssets(branchIconUrl);
      return;
    }

    applyInstitutionalBrandingAssets();
  }, [branch?.name, branchIconUrl]);

  useEffect(() => {
    if (!screenMessage.text) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setScreenMessage({ type: "", text: "" });
    }, 4200);

    return () => window.clearTimeout(timer);
  }, [screenMessage]);

  const handleSubmitCode = async (event) => {
    event.preventDefault();
    const safeCode = normalizePairingCodeInput(codeInput);

    if (!safeCode) {
      setErrorMessage("Escribe el codigo de la sucursal para vincular esta cocina.");
      return;
    }

    persistLinkedCode(safeCode);
    await loadBoard(safeCode, { quiet: false });
  };

  const handleUnlink = () => {
    persistLinkedCode("");
    setBoard(null);
    setErrorMessage("");
    setScreenMessage({ type: "", text: "" });
  };

  const handleKitchenAction = useCallback(
    async (order, action) => {
      const actionKey = `${order?.id || ""}:${action}`;
      setPendingActionKey(actionKey);
      setErrorMessage("");

      try {
        const actionResult = await applyPublicKitchenBoardAction({
          pairingCode: linkedCode,
          orderId: order?.id,
          action,
        });

        if (actionResult?.branch || actionResult?.newOrders || actionResult?.preparingOrders) {
          setBoard(actionResult);
        } else {
          setBoard((currentBoard) =>
            updateKitchenBoardAfterAction(
              currentBoard,
              order,
              action,
              actionResult?.updatedAt || new Date().toISOString()
            )
          );
        }

        if (action === "take") {
          setScreenMessage({
            type: "success",
            text: `${order?.mesa || "El pedido"} ya quedo tomada en cocina.`,
          });
        } else if (action === "ready") {
          setScreenMessage({
            type: "success",
            text: `${order?.mesa || "El pedido"} ya quedo lista para retirar.`,
          });
        } else {
          setScreenMessage({
            type: "info",
            text: `${order?.mesa || "El pedido"} volvio a preparacion.`,
          });
        }
      } catch (error) {
        setErrorMessage(
          error?.message || "No pudimos actualizar el pedido en esta cocina publica."
        );
      } finally {
        setPendingActionKey("");
      }
    },
    [linkedCode]
  );

  const pageStyle = useMemo(
    () => ({
      ...styles.page,
      background:
        branch?.primaryColor
          ? `linear-gradient(180deg, ${branch.primaryColor}12 0%, #f8fafc 28%, #eef2ff 100%)`
          : styles.page.background,
    }),
    [branch?.primaryColor]
  );

  const renderOrderCard = (order, stage) => {
    const stageMeta = getOrderStageMeta(stage);
    const actionKey =
      stage === "new"
        ? `${order.id}:take`
        : stage === "preparing"
          ? `${order.id}:ready`
          : `${order.id}:return`;
    const isPending = pendingActionKey === actionKey;

    return (
      <article key={`${stage}-${order.id}-${order.preparationNumber}`} style={styles.orderCard}>
        <div style={styles.orderHeader}>
          <div>
            <div style={styles.orderNumber}>
              {formatPreparationNumber(order.preparationNumber)}
            </div>
            <div style={styles.orderTitle}>{order.mesa || order.displayName || "Pedido"}</div>
            <div style={styles.orderMeta}>
              {order.comensales || 1} comensales · {order.canal || "Salon"}
              {order.customerName ? ` · ${order.customerName}` : ""}
            </div>
          </div>
          <span
            style={{
              ...styles.stageBadge,
              ...(stage === "new"
                ? styles.stageBadgeNew
                : stage === "preparing"
                  ? styles.stageBadgePreparing
                  : styles.stageBadgeReady),
            }}
          >
            {stageMeta.label}
          </span>
        </div>

        <div style={styles.infoGrid}>
          <div style={styles.infoCard}>
            <span style={styles.infoLabel}>Tiempo</span>
            <strong style={styles.infoValue}>{formatElapsed(order.orderedAt)}</strong>
            <span style={styles.infoHint}>{stageMeta.hint}</span>
          </div>
          <div style={styles.infoCard}>
            <span style={styles.infoLabel}>Items</span>
            <strong style={styles.infoValue}>{order.itemCount}</strong>
            <span style={styles.infoHint}>{formatCurrency(order.totalAmount)}</span>
          </div>
        </div>

        <div style={styles.itemsBlock}>
          {(order.items || []).length > 0 ? (
            order.items.map((item, index) => (
              <div key={`${order.id}-item-${index}`} style={styles.itemRow}>
                <span style={styles.itemQuantity}>{item.quantity}x</span>
                <span style={styles.itemName}>{item.name}</span>
              </div>
            ))
          ) : (
            <div style={styles.emptyItems}>No hay items detallados para este pedido.</div>
          )}
        </div>

        {order.notes ? <div style={styles.noteBox}>Notas: {order.notes}</div> : null}

        <div style={styles.cardActions}>
          {stage === "new" ? (
            <button
              type="button"
              style={styles.primaryButton}
              disabled={isPending}
              onClick={() => handleKitchenAction(order, "take")}
            >
              {isPending ? "Tomando..." : "Tomar en cocina"}
            </button>
          ) : null}

          {stage === "preparing" ? (
            <button
              type="button"
              style={styles.primaryButton}
              disabled={isPending}
              onClick={() => handleKitchenAction(order, "ready")}
            >
              {isPending ? "Guardando..." : "Marcar listo"}
            </button>
          ) : null}

          {stage === "ready" ? (
            <button
              type="button"
              style={styles.secondaryButton}
              disabled={isPending}
              onClick={() => handleKitchenAction(order, "return")}
            >
              {isPending ? "Volviendo..." : "Volver a cocina"}
            </button>
          ) : null}
        </div>
      </article>
    );
  };

  if (!linkedCode) {
    return (
      <div style={styles.setupPage}>
        <div style={styles.setupCard}>
          <div style={styles.eyebrow}>MozzoApp Cocina</div>
          <h1 style={styles.setupTitle}>Vincular cocina</h1>
          <p style={styles.setupText}>
            Escribe el codigo que ves en la configuracion de la sucursal para abrir esta
            cocina en una notebook, TV touch o mini PC dedicada.
          </p>

          <form onSubmit={handleSubmitCode} style={styles.setupForm}>
            <input
              type="text"
              value={codeInput}
              onChange={(event) => setCodeInput(normalizePairingCodeInput(event.target.value))}
              placeholder="Ej: MZ-ABCD-EF12-3456"
              style={styles.setupInput}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
            />
            <button type="submit" style={styles.primaryButton}>
              Abrir cocina
            </button>
          </form>

          {errorMessage ? <div style={styles.errorBanner}>{errorMessage}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={styles.shell}>
        <div style={styles.header}>
          <div style={styles.headerBrand}>
            {branchIconUrl ? (
              <img
                src={branchIconUrl}
                alt={branch?.name || "Sucursal"}
                style={styles.headerLogo}
              />
            ) : null}
            <div style={styles.headerCopy}>
              <div style={styles.eyebrow}>MozzoApp Cocina</div>
              <h1 style={styles.headerTitle}>{branch?.name || "Cocina publica"}</h1>
              <div style={styles.headerMeta}>
                Codigo {linkedCode} · Actualizado {formatBoardTimestamp(board?.updatedAt)}
              </div>
            </div>
          </div>

          <button type="button" onClick={handleUnlink} style={styles.secondaryButton}>
            Cambiar codigo
          </button>
        </div>

        {screenMessage.text ? (
          <section
            style={{
              ...styles.toast,
              ...(screenMessage.type === "success"
                ? styles.toastSuccess
                : screenMessage.type === "info"
                  ? styles.toastInfo
                  : {}),
            }}
          >
            {screenMessage.text}
          </section>
        ) : null}

        {errorMessage ? <div style={styles.errorBanner}>{errorMessage}</div> : null}

        <section style={styles.summaryGrid}>
          <article style={styles.summaryCard}>
            <span style={styles.summaryLabel}>Pedidos visibles</span>
            <strong style={styles.summaryValue}>{summary.total}</strong>
            <span style={styles.summaryHint}>Pantalla y comandas impresas de kiosk</span>
          </article>
          <article style={{ ...styles.summaryCard, ...styles.summaryCardNew }}>
            <span style={styles.summaryLabel}>Nuevos</span>
            <strong style={styles.summaryValue}>{summary.new}</strong>
            <span style={styles.summaryHint}>Pendientes de tomar</span>
          </article>
          <article style={{ ...styles.summaryCard, ...styles.summaryCardPreparing }}>
            <span style={styles.summaryLabel}>En cocina</span>
            <strong style={styles.summaryValue}>{summary.preparing}</strong>
            <span style={styles.summaryHint}>Tomados o impresos para preparacion</span>
          </article>
          <article style={{ ...styles.summaryCard, ...styles.summaryCardReady }}>
            <span style={styles.summaryLabel}>Listos</span>
            <strong style={styles.summaryValue}>{summary.ready}</strong>
            <span style={styles.summaryHint}>Esperando entrega o retiro</span>
          </article>
        </section>

        <section style={styles.boardGrid}>
          <section style={styles.boardColumn}>
            <div style={styles.columnHeader}>
              <div>
                <div style={styles.columnEyebrow}>Pendientes</div>
                <h2 style={styles.columnTitle}>Nuevos</h2>
              </div>
              <div style={styles.columnCount}>{summary.new}</div>
            </div>
            <div style={styles.cardsGrid}>
              {newOrders.length > 0 ? (
                newOrders.map((order) => renderOrderCard(order, "new"))
              ) : (
                <div style={styles.emptyCard}>No hay pedidos nuevos en esta cocina.</div>
              )}
            </div>
          </section>

          <section style={styles.boardColumn}>
            <div style={styles.columnHeader}>
              <div>
                <div style={styles.columnEyebrow}>En curso</div>
                <h2 style={styles.columnTitle}>Preparacion</h2>
              </div>
              <div style={styles.columnCount}>{summary.preparing}</div>
            </div>
            <div style={styles.cardsGrid}>
              {preparingOrders.length > 0 ? (
                preparingOrders.map((order) => renderOrderCard(order, "preparing"))
              ) : (
                <div style={styles.emptyCard}>Nada en preparacion ahora mismo.</div>
              )}
            </div>
          </section>

          <section style={styles.boardColumn}>
            <div style={styles.columnHeader}>
              <div>
                <div style={styles.columnEyebrow}>Salida</div>
                <h2 style={styles.columnTitle}>Listos</h2>
              </div>
              <div style={styles.columnCount}>{summary.ready}</div>
            </div>
            <div style={styles.cardsGrid}>
              {readyOrders.length > 0 ? (
                readyOrders.map((order) => renderOrderCard(order, "ready"))
              ) : (
                <div style={styles.emptyCard}>Todavia no hay pedidos listos.</div>
              )}
            </div>
          </section>
        </section>

        {loading ? <div style={styles.loadingBar}>Sincronizando cocina...</div> : null}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
    padding: "28px 18px 36px",
  },
  shell: {
    maxWidth: "1580px",
    margin: "0 auto",
    display: "grid",
    gap: "18px",
  },
  setupPage: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: "24px",
    background: "linear-gradient(180deg, #fff7ed 0%, #eff6ff 100%)",
  },
  setupCard: {
    width: "min(540px, 100%)",
    display: "grid",
    gap: "16px",
    padding: "28px",
    borderRadius: "28px",
    border: "1px solid #dbe2ea",
    background: "#ffffff",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.08)",
  },
  eyebrow: {
    display: "inline-flex",
    width: "fit-content",
    padding: "6px 10px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
  },
  setupTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "34px",
    lineHeight: 1.05,
  },
  setupText: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.7,
  },
  setupForm: {
    display: "grid",
    gap: "12px",
  },
  setupInput: {
    width: "100%",
    minHeight: "56px",
    padding: "0 16px",
    borderRadius: "16px",
    border: "1px solid #cbd5e1",
    fontSize: "18px",
    fontWeight: 700,
    letterSpacing: 0,
    color: "#0f172a",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    padding: "20px 22px",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.06)",
  },
  headerBrand: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    minWidth: 0,
  },
  headerLogo: {
    width: "56px",
    height: "56px",
    objectFit: "contain",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    padding: "8px",
  },
  headerCopy: {
    display: "grid",
    gap: "6px",
    minWidth: 0,
  },
  headerTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "32px",
    lineHeight: 1.02,
  },
  headerMeta: {
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  toast: {
    padding: "14px 16px",
    borderRadius: "16px",
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  toastSuccess: {
    border: "1px solid #bbf7d0",
    backgroundColor: "#f0fdf4",
    color: "#166534",
  },
  toastInfo: {
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  errorBanner: {
    padding: "14px 16px",
    borderRadius: "16px",
    border: "1px solid #fecaca",
    backgroundColor: "#fff5f5",
    color: "#b91c1c",
    fontWeight: 700,
    lineHeight: 1.5,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
  },
  summaryCard: {
    display: "grid",
    gap: "6px",
    padding: "18px",
    borderRadius: "20px",
    border: "1px solid #dbe2ea",
    background: "rgba(255,255,255,0.96)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.05)",
  },
  summaryCardNew: {
    border: "1px solid #fdba74",
    background: "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,247,237,0.98) 100%)",
  },
  summaryCardPreparing: {
    border: "1px solid #93c5fd",
    background: "linear-gradient(180deg, rgba(239,246,255,1) 0%, rgba(248,250,252,0.98) 100%)",
  },
  summaryCardReady: {
    border: "1px solid #86efac",
    background: "linear-gradient(180deg, rgba(240,253,244,1) 0%, rgba(248,250,252,0.98) 100%)",
  },
  summaryLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
  },
  summaryValue: {
    color: "#0f172a",
    fontSize: "28px",
    lineHeight: 1,
  },
  summaryHint: {
    color: "#475569",
    lineHeight: 1.5,
  },
  boardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px",
    alignItems: "start",
  },
  boardColumn: {
    display: "grid",
    gap: "14px",
    minHeight: "200px",
    padding: "18px",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 18px 34px rgba(15, 23, 42, 0.05)",
  },
  columnHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },
  columnEyebrow: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
  },
  columnTitle: {
    margin: "6px 0 0",
    color: "#0f172a",
    fontSize: "24px",
  },
  columnCount: {
    minWidth: "44px",
    height: "44px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    border: "1px solid #dbe2ea",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 800,
  },
  cardsGrid: {
    display: "grid",
    gap: "14px",
  },
  orderCard: {
    display: "grid",
    gap: "14px",
    padding: "18px",
    borderRadius: "22px",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
  },
  orderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  orderNumber: {
    color: "#2563eb",
    fontSize: "26px",
    fontWeight: 800,
    lineHeight: 1,
  },
  orderTitle: {
    marginTop: "8px",
    color: "#0f172a",
    fontSize: "24px",
    fontWeight: 800,
    lineHeight: 1.05,
  },
  orderMeta: {
    marginTop: "6px",
    color: "#64748b",
    lineHeight: 1.5,
  },
  stageBadge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: "999px",
    fontWeight: 800,
    fontSize: "13px",
    textAlign: "center",
  },
  stageBadgeNew: {
    border: "1px solid #fdba74",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
  },
  stageBadgePreparing: {
    border: "1px solid #93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  stageBadgeReady: {
    border: "1px solid #86efac",
    backgroundColor: "#f0fdf4",
    color: "#166534",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "10px",
  },
  infoCard: {
    display: "grid",
    gap: "4px",
    padding: "12px 14px",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  infoLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
  },
  infoValue: {
    color: "#0f172a",
    fontSize: "20px",
    lineHeight: 1.1,
  },
  infoHint: {
    color: "#475569",
    lineHeight: 1.4,
  },
  itemsBlock: {
    display: "grid",
    gap: "8px",
  },
  itemRow: {
    display: "grid",
    gridTemplateColumns: "56px 1fr",
    gap: "10px",
    alignItems: "start",
    padding: "10px 12px",
    borderRadius: "14px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  itemQuantity: {
    color: "#2563eb",
    fontWeight: 800,
    fontSize: "18px",
  },
  itemName: {
    color: "#0f172a",
    fontWeight: 700,
    lineHeight: 1.45,
  },
  emptyItems: {
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    backgroundColor: "#f8fafc",
  },
  noteBox: {
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid #fde68a",
    backgroundColor: "#fffbeb",
    color: "#92400e",
    lineHeight: 1.5,
    fontWeight: 700,
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  primaryButton: {
    minHeight: "48px",
    padding: "0 18px",
    border: "none",
    borderRadius: "14px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontSize: "16px",
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryButton: {
    minHeight: "48px",
    padding: "0 18px",
    border: "1px solid #cbd5e1",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: "16px",
    fontWeight: 800,
    cursor: "pointer",
  },
  emptyCard: {
    minHeight: "180px",
    display: "grid",
    placeItems: "center",
    padding: "22px",
    borderRadius: "20px",
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    color: "#64748b",
    textAlign: "center",
    lineHeight: 1.6,
  },
  loadingBar: {
    padding: "12px 14px",
    borderRadius: "16px",
    border: "1px solid #dbeafe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 700,
  },
};

export default CocinaPublica;
